/* ============================================================
   KAIRII — Reference port: gimmick behaviors (vanilla JS)
   Holo tilt · Scouter scan · Eyecatch nav · Konami SSJ ·
   scroll reveal · turntable scrub. Ported from the React app.
   All effects respect prefers-reduced-motion and theme toggles.
   ============================================================ */
(function () {
  'use strict';

  var R = window.__krRefI18n || {};
  var root = document.documentElement;
  var reduceMotion = false;
  
  function on(attr) {
    return root.getAttribute(attr) === 'on';
  }

  /* ----------------------------------------------------------
     1) HOLO TILT  — pointer-tracked 3D tilt + foil sweep
     ---------------------------------------------------------- */
  function initHoloCard(card) {
    if (card.__krHolo) return;
    card.__krHolo = true;
    var media = card.querySelector('.kr-card__media');
    if (!media) return;
    var MAX = 11;

    function move(e) {
      if (!on('data-kr-holo') || reduceMotion) return;
      var r = media.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width;
      var py = (e.clientY - r.top) / r.height;
      var ry = (px - 0.5) * 2 * MAX;
      var rx = (0.5 - py) * 2 * MAX;
      card.classList.add('tilting');
      media.style.setProperty('--rx', ry.toFixed(2) + 'deg');
      media.style.setProperty('--ry', rx.toFixed(2) + 'deg');
      var foil = card.querySelector('.kr-card__foil');
      if (foil) {
        foil.style.setProperty('--fx', (px * 100).toFixed(1) + '%');
        foil.style.setProperty('--fy', (py * 100).toFixed(1) + '%');
      }
    }
    function leave() {
      card.classList.remove('tilting');
      media.style.setProperty('--rx', '0deg');
      media.style.setProperty('--ry', '0deg');
    }
    media.addEventListener('pointermove', move);
    media.addEventListener('pointerleave', leave);
  }

  /* ----------------------------------------------------------
     2) SCOUTER HUD  — scans figure when it enters the viewport
     ---------------------------------------------------------- */
  function initScouter(scouter) {
    if (scouter.__krScouter) return;
    scouter.__krScouter = true;
    var valEl = scouter.querySelector('.kr-scouter__val');
    var target = parseInt(scouter.getAttribute('data-power') || '9000', 10);

    function runScan() {
      if (!on('data-kr-scouter')) return;
      scouter.classList.remove('locked');
      scouter.classList.add('scan');
      if (reduceMotion) {
        if (valEl) valEl.textContent = target.toLocaleString();
        scouter.classList.remove('scan');
        scouter.classList.add('locked');
        return;
      }
      var start = performance.now();
      var DUR = 1150;
      function tick(now) {
        var p = Math.min((now - start) / DUR, 1);
        if (valEl) {
          if (p < 1) {
            var jitter = Math.floor(target * (0.4 + Math.random() * 0.9));
            valEl.textContent = jitter.toLocaleString();
          } else {
            valEl.textContent = target.toLocaleString();
          }
        }
        if (p < 1) {
          requestAnimationFrame(tick);
        } else {
          scouter.classList.remove('scan');
          scouter.classList.add('locked');
        }
      }
      requestAnimationFrame(tick);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          runScan();
          io.unobserve(scouter);
        }
      });
    }, { threshold: 0.4 });
    io.observe(scouter);

    var rescan = scouter.querySelector('.kr-scouter__rescan');
    if (rescan) rescan.addEventListener('click', runScan);
  }

  /* ----------------------------------------------------------
     3) SCROLL REVEAL
     ---------------------------------------------------------- */
  function initReveal() {
    var els = document.querySelectorAll('.kr-reveal');
    if (!els.length) return;
    if (reduceMotion) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          en.target.classList.add('in');
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ----------------------------------------------------------
     4) EYECATCH route transition on internal navigation
     ---------------------------------------------------------- */
  function initEyecatch() {
    if (reduceMotion || !on('data-kr-eyecatch')) return;
    if (window.Shopify && window.Shopify.designMode) return;

    function buildOverlay() {
      var overlay = document.createElement('div');
      overlay.className = 'kr-eyecatch';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.innerHTML =
        '<div class="kr-eyecatch__mark">' +
        '<span class="kr-logo">Ka<span class="kr-dot-i">i</span>ri<span class="kr-dot-i">i</span></span>' +
        '<span class="kr-jp">アイキャッチ</span>' +
        '</div>';
      document.body.appendChild(overlay);
      return overlay;
    }

    // Reveal phase: if the previous page set the flag, uncover smoothly.
    function playReveal() {
      var revealEl = window.__krEyeHold;
      if (revealEl) {
        // Overlay was placed pre-paint by the inline boot script — reveal it.
        window.__krEyeHold = null;
      } else {
        try {
          if (sessionStorage.getItem('krEye') !== '1') return;
        } catch (err) { return; }
        revealEl = buildOverlay();
      }
      try { sessionStorage.removeItem('krEye'); } catch (err) {}
      revealEl.classList.remove('kr-eyecatch--hold');
      void revealEl.offsetWidth;
      revealEl.classList.add('cover-out');
      revealEl.addEventListener('animationend', function () { revealEl.remove(); });
      setTimeout(function () { if (revealEl.parentNode) revealEl.remove(); }, 900);
    }
    playReveal();

    // Bind global handlers once (boot() can re-run on shopify:section:load).
    if (initEyecatch.__bound) return;
    initEyecatch.__bound = true;

    var overlay = buildOverlay();
    var navigating = false;

    // Cover the screen, then navigate once fully covered so the wipe completes
    // smoothly. The next page reveals it via the krEye flag.
    function coverThenGo(targetHref) {
      if (navigating) return;
      navigating = true;
      try { sessionStorage.setItem('krEye', '1'); } catch (err) {}
      overlay.classList.remove('cover-out');
      void overlay.offsetWidth;
      overlay.classList.add('cover-in');
      // Navigate the instant the curtain is fully down (matches cover-in duration),
      // so the new page's held cover continues the same motion with no pause.
      setTimeout(function () { window.location.href = targetHref; }, 360);
    }

    document.addEventListener('click', function (e) {
      if (navigating || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      var href = null;
      var a = e.target.closest && e.target.closest('a');
      if (a) {
        var h = a.getAttribute('href');
        if (!h || h.charAt(0) === '#' || a.target === '_blank' || a.hasAttribute('download')) return;
        // Collection filter/facet/sort/pagination links re-render the same page —
        // don't play the full eyecatch route transition for those (annoying flicker).
        if (a.closest('.kr-facet, .kr-filters, [data-kr-filters], .kr-pagination, [data-kr-sort], [data-kr-search-open]')) return;
        href = a.href;
      } else {
        // Card click that missed the overlay anchor (hover transforms can make
        // the absolutely-positioned anchor an unreliable hit target). Ignore
        // add-to-cart and other interactive controls inside the card.
        if (e.target.closest('button, .kr-quick-form, input, select, textarea, label')) return;
        var card = e.target.closest('.kr-card[data-kr-href]');
        if (card) href = card.getAttribute('data-kr-href');
      }
      if (!href) return;

      var url;
      try { url = new URL(href, window.location.href); } catch (err) { return; }
      // Links marked [data-kr-exit] (e.g. the account link, which on "new customer
      // accounts" points to Shopify's hosted login on a different origin) still get
      // a smooth cover-out from our side before the browser leaves the page.
      var isExit = a && a.hasAttribute('data-kr-exit');
      if (url.origin !== window.location.origin && !isExit) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      e.preventDefault();
      coverThenGo(url.href);
    });

    // Restoring from the back/forward (bfcache) keeps the cover applied — clear
    // it and play a clean reveal so returning to the homepage is not stuck.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      navigating = false;
      overlay.classList.remove('cover-in');
      try { sessionStorage.removeItem('krEye'); } catch (err) {}
      overlay.classList.remove('cover-out');
      void overlay.offsetWidth;
      overlay.classList.add('cover-out');
    });
  }

  /* ----------------------------------------------------------
     5) KONAMI — Super Saiyan easter egg
     ---------------------------------------------------------- */
  function initKonami() {
    var seq = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    var idx = 0;
    document.addEventListener('keydown', function (e) {
      var key = e.key;
      if (key === seq[idx] || key.toLowerCase() === seq[idx]) {
        idx++;
        if (idx === seq.length) { idx = 0; toggleSSJ(); }
      } else {
        idx = (key === seq[0]) ? 1 : 0;
      }
    });
  }

  function toggleSSJ() {
    var active = root.getAttribute('data-kr-ssj') === 'on';
    if (active) {
      root.setAttribute('data-kr-ssj', 'off');
      showToast(R.ssjOff || 'Power suppressed \u00b7 back to base');
      return;
    }
    root.setAttribute('data-kr-ssj', 'on');
    if (!reduceMotion) {
      var flash = document.createElement('div');
      flash.className = 'kr-ssj-flash go';
      document.body.appendChild(flash);
      var aura = document.createElement('div');
      aura.className = 'kr-ssj-aura go';
      for (var i = 0; i < 14; i++) {
        var s = document.createElement('span');
        s.style.left = (Math.random() * 100) + '%';
        s.style.animationDelay = (Math.random() * 1.2) + 's';
        aura.appendChild(s);
      }
      document.body.appendChild(aura);
      setTimeout(function () { flash.remove(); }, 1000);
      setTimeout(function () { aura.remove(); }, 4200);
    }
    showToast('\u754c\u738b\u62f3 \u00b7 Super Saiyan unlocked');
  }

  /* ----------------------------------------------------------
     6) TOAST
     ---------------------------------------------------------- */
  var toastEl, toastTimer;
  function showToast(msg, linkHtml) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'kr-toast';
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML =
      '<span class="kr-ok" aria-hidden="true">\u2713</span>' +
      '<span>' + msg + '</span>' +
      (linkHtml || '');
    void toastEl.offsetWidth;
    toastEl.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('show'); }, 3200);
  }
  window.krShowToast = showToast;

  /* ----------------------------------------------------------
     6b) QUICK ADD — AJAX add-to-cart from product cards
         Posts to the cart, then silently refreshes the header
         count bubble. No page reload, no drawer.
     ---------------------------------------------------------- */
  function currentCartCount() {
    var bubbleHost = document.getElementById('cart-icon-bubble');
    if (!bubbleHost) return 0;
    var num = bubbleHost.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    var n = num ? parseInt(num.textContent, 10) : 0;
    return isNaN(n) ? 0 : n;
  }

  function setCartCount(count) {
    var bubbleHost = document.getElementById('cart-icon-bubble');
    if (!bubbleHost) return;
    var bubble = bubbleHost.querySelector('.cart-count-bubble');
    if (count > 0 && !bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true"></span>';
      bubbleHost.appendChild(bubble);
    }
    if (!bubble) return;
    if (count <= 0) { bubble.remove(); return; }
    var num = bubble.querySelector('span[aria-hidden="true"]');
    if (!num) {
      num = document.createElement('span');
      num.setAttribute('aria-hidden', 'true');
      bubble.insertBefore(num, bubble.firstChild);
    }
    num.textContent = count < 100 ? count : '';
  }

  // Swap the freshly-rendered header markup from a Section Rendering API
  // response into the live mini-cart, then briefly reveal it so the new piece
  // is visible without the user having to hover.
  var krCartHideTimer;
  function refreshCartPreview(response, sectionId) {
    var wrap = document.querySelector('[data-cart-wrap]');
    var html = response && response.sections ? response.sections[sectionId] : null;
    if (wrap && html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var freshWrap = doc.querySelector('[data-cart-wrap]');
      if (freshWrap) {
        // Replace only the inner contents (icon link + preview) so the wrapper's
        // hover/open state and listeners on .kr-cart-wrap itself are preserved.
        wrap.innerHTML = freshWrap.innerHTML;
        // Highlight the top line item (most-recently-added sits first).
        var firstItem = wrap.querySelector('.kr-cart-preview__item');
        if (firstItem) firstItem.classList.add('kr-just-added');
        // Auto-reveal the preview, then hand control back to :hover.
        wrap.classList.add('is-open');
        clearTimeout(krCartHideTimer);
        krCartHideTimer = setTimeout(function () { wrap.classList.remove('is-open'); }, 2600);
        wrap.addEventListener('mouseleave', function () {
          clearTimeout(krCartHideTimer);
          wrap.classList.remove('is-open');
        }, { once: true });
        return;
      }
    }
    // Fallback: no section markup — at least reconcile the count from cart.js.
    fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { setCartCount(cart.item_count); })
      .catch(function () {});
  }

  function initQuickAdd() {
    document.querySelectorAll('.kr-quick-form').forEach(function (form) {
      if (form.__krQuick) return;
      form.__krQuick = true;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('button[type="submit"]');
        if (btn && btn.getAttribute('aria-busy') === 'true') return;
        var label = btn ? btn.innerHTML : '';
        if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }

        // Optimistic UI: bump the count + toast instantly, reconcile after.
        var qtyField = form.querySelector('[name="quantity"]');
        var qty = qtyField ? parseInt(qtyField.value, 10) : 1;
        if (isNaN(qty) || qty < 1) qty = 1;
        var prevCount = currentCartCount();
        setCartCount(prevCount + qty);
        var cartRoot = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
        showToast(R.added || 'Added to cart', '<a class="kr-toast__link" href="' + cartRoot + 'cart">' + (R.viewCart || 'View cart') + '</a>');
        if (btn) { btn.innerHTML = (R.addedShort || 'Added') + ' \u2713'; }
        var resetBtn = function () {
          if (btn) { btn.innerHTML = label; btn.removeAttribute('aria-busy'); btn.disabled = false; }
        };
        setTimeout(resetBtn, 1400);

        var data = new FormData(form);

        // Ask the cart/add call to also render the header section back to us, so
        // the hover mini-cart (count, "Due today", deposit lines, thumbnails) is
        // recomputed server-side in the SAME round-trip — no second cart.js fetch,
        // no full reload. This is what makes the add feel instant.
        var cartWrap = document.querySelector('[data-cart-wrap]');
        var sectionId = cartWrap ? cartWrap.getAttribute('data-section-id') : null;
        if (sectionId) {
          data.append('sections', sectionId);
          data.append('sections_url', window.location.pathname);
        }

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: data
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (err) { throw err; });
            return r.json();
          })
          .then(function (response) {
            refreshCartPreview(response, sectionId);
          })
          .catch(function (err) {
            // Roll back the optimistic bump.
            setCartCount(prevCount);
            showToast((err && err.description) || R.addError || 'Could not add to cart');
            resetBtn();
          });
      });
    });
  }

  /* ----------------------------------------------------------
     7) ACCORDION (product page)
     ---------------------------------------------------------- */
  function initAccordions() {
    document.querySelectorAll('.kr-acc__head').forEach(function (head) {
      if (head.__krAcc) return;
      head.__krAcc = true;
      head.addEventListener('click', function () {
        var item = head.closest('.kr-acc__item');
        if (item) item.classList.toggle('open');
        var expanded = item && item.classList.contains('open');
        head.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    });
  }

  /* ----------------------------------------------------------
     8) TURNTABLE — fallback carousel scrub + play/pause
     (native model-viewer auto-rotates on its own)
     ---------------------------------------------------------- */
  function initTurntable(stage) {
    if (stage.__krTt) return;
    stage.__krTt = true;
    var scrub = stage.parentElement.querySelector('.kr-tt-scrub');
    var playBtn = stage.parentElement.querySelector('.kr-tt-play');
    var angleEl = stage.parentElement.querySelector('.kr-tt-angle');
    var mv = stage.querySelector('model-viewer');

    if (mv) {
      // Reveal the figure with a fade once the GLB is ready (no abrupt pop-in).
      function markLoaded() { stage.classList.add('is-loaded'); }
      if (mv.loaded) { markLoaded(); }
      else { mv.addEventListener('load', markLoaded); }
      // Safety net: never leave the loader spinning forever if 'load' is missed.
      setTimeout(markLoaded, 8000);

      var scrubbing = false;

      // model-viewer's `auto-rotate` does NOT change the camera orbit (theta stays
      // put) — it spins the model via an internal turntableRotation, and
      // 'camera-change' does not fire while it auto-rotates. So poll turntableRotation
      // (plus the base camera theta) every frame to keep the slider + readout synced.
      function currentDeg() {
        var base = 0;
        if (typeof mv.getCameraOrbit === 'function') {
          base = mv.getCameraOrbit().theta * 180 / Math.PI;
        }
        var turn = (typeof mv.turntableRotation === 'number') ? mv.turntableRotation * 180 / Math.PI : 0;
        return ((base + turn) % 360 + 360) % 360;
      }
      function syncUi() {
        if (!scrubbing) {
          var deg = Math.round(currentDeg());
          if (scrub) scrub.value = deg;
          if (angleEl) angleEl.textContent = deg + '\u00b0';
        }
        requestAnimationFrame(syncUi);
      }
      requestAnimationFrame(syncUi);

      // Manual drag of the camera (user orbiting) — keep UI in sync too.
      mv.addEventListener('camera-change', function () {
        if (scrubbing) return;
        var deg = Math.round(currentDeg());
        if (scrub) scrub.value = deg;
        if (angleEl) angleEl.textContent = deg + '\u00b0';
      });

      // Live GLB — wire scrubber to camera orbit, play toggles auto-rotate.
      if (scrub) {
        // While dragging, pause auto-rotate and drive the orbit manually.
        scrub.addEventListener('pointerdown', function () {
          scrubbing = true;
          mv.removeAttribute('auto-rotate');
          if (playBtn) playBtn.setAttribute('data-playing', 'false');
        });
        var endScrub = function () { scrubbing = false; };
        scrub.addEventListener('pointerup', endScrub);
        scrub.addEventListener('pointercancel', endScrub);
        scrub.addEventListener('change', endScrub);
        scrub.addEventListener('input', function () {
          scrubbing = true;
          // Displayed angle = camera theta + turntableRotation. To make the model
          // land exactly on the slider value, subtract the frozen turntable offset.
          var turn = (typeof mv.turntableRotation === 'number') ? mv.turntableRotation * 180 / Math.PI : 0;
          mv.cameraOrbit = (parseFloat(scrub.value) - turn) + 'deg 82deg auto';
          if (angleEl) angleEl.textContent = scrub.value + '\u00b0';
        });
      }
      if (playBtn) {
        playBtn.addEventListener('click', function () {
          var playing = mv.hasAttribute('auto-rotate');
          if (playing) { mv.removeAttribute('auto-rotate'); playBtn.setAttribute('data-playing', 'false'); }
          else {
            // Resume continuous spin from the current angle.
            mv.setAttribute('auto-rotate-delay', '0');
            mv.setAttribute('auto-rotate', '');
            playBtn.setAttribute('data-playing', 'true');
          }
        });
      }
      return;
    }

    // Placeholder carousel: 48 frames, drag/scrub to rotate.
    var FRAMES = 48;
    var angle = 0;
    var playing = !reduceMotion;
    var dragging = false, lastX = 0;
    var raf;

    function render() {
      var deg = ((angle % 360) + 360) % 360;
      if (angleEl) angleEl.textContent = Math.round(deg) + '\u00b0';
      if (scrub) scrub.value = Math.round(deg);
      var obj = stage.querySelector('.kr-stage360__obj');
      if (obj) obj.style.transform = 'rotateY(' + (deg * 0.12) + 'deg)';
    }
    function loop() {
      if (playing && !dragging) { angle += 0.6; render(); }
      raf = requestAnimationFrame(loop);
    }
    stage.addEventListener('pointerdown', function (e) {
      dragging = true; lastX = e.clientX; stage.classList.add('dragging');
      stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      angle += (e.clientX - lastX) * 0.6; lastX = e.clientX; render();
    });
    function endDrag() { dragging = false; stage.classList.remove('dragging'); }
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);
    if (scrub) scrub.addEventListener('input', function () { angle = parseFloat(scrub.value); playing = false; render(); });
    if (playBtn) playBtn.addEventListener('click', function () { playing = !playing; playBtn.setAttribute('data-playing', playing ? 'true' : 'false'); });
    render();
    if (!reduceMotion) loop();
  }

  /* ----------------------------------------------------------
     9) PDP GALLERY — thumb swap, Gallery/360 toggle, qty stepper,
     variant selection (updates hidden id + price)
     ---------------------------------------------------------- */
  function initPdpGallery(pdp) {
    if (pdp.__krPdp) return;
    pdp.__krPdp = true;

    var mainImg = pdp.querySelector('[data-kr-main-img]');
    var thumbs = pdp.querySelectorAll('[data-kr-thumb]');

    // Warm the browser cache with every full-size image up front so clicking a
    // thumbnail swaps instantly instead of triggering a fresh network fetch.
    thumbs.forEach(function (thumb) {
      var full = thumb.getAttribute('data-src');
      if (full) { var pre = new Image(); pre.decoding = 'async'; pre.src = full; }
    });

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-src');
        if (mainImg && src && mainImg.src !== src) {
          // Clear srcset/sizes so the browser uses the newly set src instead of
          // re-picking the original image from the responsive candidate list.
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          // Decode off-thread before painting to avoid a janky mid-swap flash;
          // the image is already cached from the preload above so this is instant.
          var next = new Image();
          next.decoding = 'async';
          next.src = src;
          var apply = function () { mainImg.src = src; };
          if (next.decode) { next.decode().then(apply).catch(apply); }
          else { apply(); }
        }
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
        // Switching a thumbnail returns to the gallery pane.
        setMode('gallery');
      });
    });

    // Gallery / 360 mode toggle
    var modeBtns = pdp.querySelectorAll('[data-kr-mode]');
    var panes = pdp.querySelectorAll('[data-kr-pane]');
    var mv = pdp.querySelector('.kr-pdp__mv');
    function setMode(mode) {
      modeBtns.forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-kr-mode') === mode); });
      panes.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-kr-pane') === mode); });
      if (mode === '360' && mv && !mv.__krReveal) {
        mv.__krReveal = true;
        if (typeof mv.dismissPoster === 'function') { mv.dismissPoster(); }
        mv.setAttribute('reveal', 'auto');
      }
    }
    modeBtns.forEach(function (b) {
      b.addEventListener('click', function () { setMode(b.getAttribute('data-kr-mode')); });
    });

    // Quantity stepper
    var qtyInput = pdp.querySelector('.kr-qty__input');
    pdp.querySelectorAll('[data-kr-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var v = parseInt(qtyInput.value, 10) || 1;
        v += btn.getAttribute('data-kr-qty') === 'up' ? 1 : -1;
        if (v < 1) v = 1;
        qtyInput.value = v;
      });
    });

    // Variant selection
    var variantData = pdp.querySelector('[data-kr-variants]');
    var idInput = pdp.querySelector('.product-variant-id');
    var priceEl = pdp.querySelector('[data-kr-price]');
    var addBtn = pdp.querySelector('.kr-pdp__add');
    if (variantData && idInput) {
      var variants;
      try { variants = JSON.parse(variantData.textContent); } catch (e) { variants = []; }
      var optionGroups = pdp.querySelectorAll('.kr-pdp__option');
      function selectedOptions() {
        return Array.prototype.map.call(optionGroups, function (grp) {
          var checked = grp.querySelector('input:checked');
          return checked ? checked.value : null;
        });
      }
      function updateVariant() {
        var sel = selectedOptions();
        var match = variants.find(function (v) {
          return sel.every(function (val, i) { return v.options[i] === val; });
        });
        if (!match) return;
        idInput.value = match.id;
        if (priceEl && match.price != null) {
          priceEl.textContent = formatMoney(match.price);
        }
        if (addBtn) {
          addBtn.disabled = !match.available;
          idInput.disabled = !match.available;
          var span = addBtn.querySelector('span');
          if (span) span.textContent = match.available ? 'Add to cart' : 'Sold out';
        }
      }
      optionGroups.forEach(function (grp) {
        grp.querySelectorAll('input').forEach(function (input) {
          input.addEventListener('change', function () {
            grp.querySelectorAll('.kr-pdp__swatch').forEach(function (s) {
              s.classList.toggle('is-active', s.contains(input) && input.checked);
            });
            updateVariant();
          });
        });
      });
    }
  }

  // Shopify money: prices are in cents. Mirror the store's money format minimally.
  function formatMoney(cents) {
    var amount = (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    var sample = (root.getAttribute('data-kr-money') || '€{{amount}}');
    return sample.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{[^}]*\}\}/g, amount);
  }

  /* ----------------------------------------------------------
     BOOT
     ---------------------------------------------------------- */
  function boot() {
    document.querySelectorAll('.kr-card').forEach(initHoloCard);
    document.querySelectorAll('.kr-scouter').forEach(initScouter);
    document.querySelectorAll('.kr-stage360').forEach(initTurntable);
    document.querySelectorAll('[data-kr-pdp]').forEach(initPdpGallery);
    initAccordions();
    initReveal();
    initEyecatch();
    initQuickAdd();
    if (root.getAttribute('data-kr-konami') === 'on') initKonami();
  }

  // Exposed so AJAX-swapped content (e.g. collection filtering) can re-init the
  // holographic cards + quick-add on freshly injected markup. boot() is idempotent.
  window.krBoot = boot;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', boot);
})();
